// OTP Verification component
// Email verification step with 6-digit OTP input
import React, { useState, useEffect, useRef } from 'react'

interface Props {
  email: string
  onVerified: (token: string) => void
  onBack?: () => void
}

export default function OTPVerification({ email, onVerified, onBack }: Props) {
  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState<'error' | 'success' | 'info'>('info')
  const [loading, setLoading] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Auto-focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus()
  }, [])

  // Handle OTP input change
  const handleOtpChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return

    const newOtp = [...otp]
    newOtp[index] = value
    setOtp(newOtp)

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }

    // Clear message when user starts typing
    if (message) setMessage('')
  }

  // Handle backspace
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  // Handle paste
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').trim().slice(0, 6)
    if (/^\d{1,6}$/.test(pastedData)) {
      const newOtp = [...otp]
      for (let i = 0; i < 6; i++) {
        newOtp[i] = pastedData[i] || ''
      }
      setOtp(newOtp)
      // Focus last filled input or submit button
      const lastFilledIndex = Math.min(pastedData.length - 1, 5)
      inputRefs.current[lastFilledIndex]?.focus()
    }
  }

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    const otpString = otp.join('')
    
    if (otpString.length !== 6) {
      setMessage('Please enter all 6 digits')
      setMessageType('error')
      return
    }

    setLoading(true)
    setMessage('')

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
      const response = await fetch(`${apiUrl}/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp: otpString })
      })

      const data = await response.json()
      if (!response.ok) {
        setMessageType('error')
        setMessage(data.message || 'Invalid or expired OTP. Please try again.')
        // Clear OTP on error
        setOtp(['', '', '', '', '', ''])
        inputRefs.current[0]?.focus()
      } else {
        setMessageType('success')
        setMessage('✅ Email verified successfully! Redirecting...')
        setTimeout(() => onVerified(data.token), 1000)
      }
    } catch (err) {
      console.error(err)
      setMessageType('error')
      setMessage('Server error. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async () => {
    if (resendCooldown > 0) return

    setLoading(true)
    setMessage('')
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
      const res = await fetch(`${apiUrl}/auth/resend-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      })
      const data = await res.json()
      if (res.ok) {
        const emailSent = data.verificationEmailSent !== false
        setMessageType(emailSent ? 'success' : 'info')
        setMessage(
          emailSent
            ? '📨 New OTP sent to your email!'
            : "⚠️ OTP was updated but the email could not be sent. Check server email configuration (RESEND_API_KEY or SMTP)."
        )
        setResendCooldown(60) // 60 second cooldown
        setOtp(['', '', '', '', '', ''])
        inputRefs.current[0]?.focus()
      } else {
        setMessageType('error')
        setMessage(data.message || 'Failed to resend OTP. Please try again.')
      }
    } catch (err) {
      console.error(err)
      setMessageType('error')
      setMessage('Server error. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const otpString = otp.join('')

  return (
    <div>
      {/* Header */}
      <div className="text-center mb-8">
        {/* Icon */}
        <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        
        <h2 className="text-2xl sm:text-3xl font-display text-stone-900 mb-2">
          Verify Your Email
        </h2>
        <p className="text-stone-600">
          We sent a 6-digit code to
        </p>
        <p className="text-stone-900 font-semibold mt-1">
          {email}
        </p>
      </div>

      {/* OTP Input Form */}
      <form onSubmit={handleVerify} className="space-y-6">
        {/* OTP Input Boxes */}
        <div className="flex justify-center gap-3">
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              className={`w-12 h-14 text-center text-2xl font-bold border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 ${
                digit
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700 focus:ring-emerald-500'
                  : 'border-stone-300 bg-white text-stone-900 focus:border-emerald-500 focus:ring-emerald-500'
              } ${messageType === 'error' ? 'border-red-400' : ''}`}
              required
              disabled={loading}
            />
          ))}
        </div>

        {/* Message Display */}
        {message && (
          <div className={`p-4 rounded-xl text-sm font-medium ${
            messageType === 'error'
              ? 'bg-red-50 text-red-700 border border-red-200'
              : messageType === 'success'
              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
              : 'bg-blue-50 text-blue-700 border border-blue-200'
          }`}>
            <div className="flex items-center justify-center gap-2">
              {messageType === 'success' && (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
              {messageType === 'error' && (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              )}
              <span>{message}</span>
            </div>
          </div>
        )}

        {/* Verify Button */}
        <button
          type="submit"
          disabled={loading || otpString.length !== 6}
          className="w-full bg-emerald-600 text-white py-3.5 px-4 rounded-xl hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/25 flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              <span>Verifying...</span>
            </>
          ) : (
            <>
              <span>Verify OTP</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </>
          )}
        </button>
      </form>

      {/* Footer Actions */}
      <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm">
        <button
          type="button"
          onClick={handleResend}
          disabled={loading || resendCooldown > 0}
          className="text-emerald-600 hover:text-emerald-700 font-medium transition-colors disabled:text-stone-400 disabled:cursor-not-allowed flex items-center gap-1"
        >
          {resendCooldown > 0 ? (
            <>
              <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Resend in {resendCooldown}s</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Resend OTP</span>
            </>
          )}
        </button>

        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="text-stone-500 hover:text-stone-700 font-medium transition-colors flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span>Back</span>
          </button>
        )}
      </div>

      {/* Help Text */}
      <div className="mt-6 p-4 bg-stone-50 rounded-xl border border-stone-200">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-stone-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-xs text-stone-600">
            <p className="font-medium mb-1">Didn't receive the code?</p>
            <p>Check your spam folder or click "Resend OTP" to get a new code. The code expires in 10 minutes.</p>
          </div>
        </div>
      </div>
    </div>
  )
}
